import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform, Dimensions } from 'react-native';
import { XStack, YStack, Button, Input, Select, Adapt, Sheet, Spinner } from 'tamagui';
import { AntDesign, Feather, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';
import ErrorBoundary from '@utils/ErrorBoundary';
import { t } from 'i18next';
import { generateColumnsFromData, TableColumn } from './tableUtils';

interface ProductionDataTableProps {
  data: any[];
  columns?: TableColumn[];
  loading?: boolean;
  onRowClick?: (row: any) => void;
  onRowEdit?: (row: any) => void;
  onRowDelete?: (row: any) => void;
  onSelectionChange?: (selectedRows: any[]) => void;
  onBulkAction?: (action: string, selectedRows: any[]) => void;
  enableSelection?: boolean;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableExport?: boolean;
  enableInlineActions?: boolean;
  pageSize?: number;
  className?: string;
  style?: any;
  emptyStateMessage?: string;
  showRowNumbers?: boolean;
  stickyHeader?: boolean;
  virtualScrolling?: boolean;
}

const ProductionDataTable: React.FC<ProductionDataTableProps> = ({
  data = [],
  columns: propColumns,
  loading = false,
  onRowClick,
  onRowEdit,
  onRowDelete,
  onSelectionChange,
  onBulkAction,
  enableSelection = true,
  enableSorting = true,
  enableFiltering = true,
  enableExport = true,
  enableInlineActions = true,
  pageSize = 50,
  className = '',
  style = {},
  emptyStateMessage = 'No data available',
  showRowNumbers = false,
  stickyHeader = true,
  virtualScrolling = false
}) => {
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const scrollViewRef = useRef<ScrollView>(null);

  // State management
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  // Generate columns if not provided
  const columns = useMemo(() => {
    if (propColumns && propColumns.length > 0) {
      return propColumns;
    }
    return generateColumnsFromData(data);
  }, [propColumns, data]);

  // Initialize column visibility
  useEffect(() => {
    const initialVisibility: Record<string, boolean> = {};
    columns.forEach(col => {
      initialVisibility[col.key] = true;
    });
    setColumnVisibility(initialVisibility);
  }, [columns]);

  // Filter visible columns
  const visibleColumns = columns.filter(col => columnVisibility[col.key] !== false);

  // Memoized processed data
  const processedData = useMemo(() => {
    let filteredData = [...data];

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(row =>
        visibleColumns.some(col => {
          const value = row[col.key];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filteredData = filteredData.filter(row => {
          const cellValue = row[key];
          return cellValue && cellValue.toString().toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig) {
      filteredData.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredData;
  }, [data, searchTerm, filters, sortConfig, visibleColumns]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = processedData.slice(startIndex, endIndex);

  // Handlers
  const handleSort = useCallback((key: string) => {
    if (!enableSorting) return;

    setSortConfig(prev => {
      if (prev?.key === key) {
        return prev.direction === 'asc'
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  }, [enableSorting]);

  const handleRowClick = useCallback((row: any) => {
    if (onRowClick) {
      onRowClick(row);
    }
  }, [onRowClick]);

  const handleRowSelect = useCallback((rowId: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRows(newSelected);

    if (onSelectionChange) {
      const selectedData = data.filter(row => newSelected.has(row._id || row.id));
      onSelectionChange(selectedData);
    }
  }, [selectedRows, onSelectionChange, data]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedData.map(row => row._id || row.id));
      setSelectedRows(allIds);
      if (onSelectionChange) {
        onSelectionChange(paginatedData);
      }
    } else {
      setSelectedRows(new Set());
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  }, [paginatedData, onSelectionChange]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(() => {
    if (!enableExport) return;

    const csvContent = [
      visibleColumns.map(col => col.title).join(','),
      ...processedData.map(row =>
        visibleColumns.map(col => {
          const value = row[col.key];
          return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `data-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [processedData, visibleColumns, enableExport]);

  const handleBulkActionClick = useCallback((action: string) => {
    const selectedData = data.filter(row => selectedRows.has(row._id || row.id));
    if (onBulkAction) {
      onBulkAction(action, selectedData);
    }
    setShowBulkActions(false);
  }, [selectedRows, data, onBulkAction]);

  const toggleColumnVisibility = useCallback((columnKey: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  }, []);

  // Reset pagination when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <Spinner size="large" />
        <Text style={{ fontSize: 16, color: theme ? '#333' : '#fff', marginTop: 16 }}>
          Loading data...
        </Text>
      </View>
    );
  }

  return (
    <ErrorBoundary>
      <View style={{ flex: 1, backgroundColor: theme ? '#fff' : '#1a1a1a', borderRadius: 12, overflow: 'hidden', ...style }}>
        {/* Header with controls */}
        <View style={{
          padding: 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e0e0e0' : '#333',
          backgroundColor: theme ? '#f8f9fa' : '#2a2a2a'
        }}>
          <XStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={12}>
            {/* Search */}
            <XStack alignItems="center" gap={8} flex={1} minWidth={200}>
              <AntDesign name="search1" size={16} color={theme ? '#666' : '#ccc'} />
              <Input
                placeholder="Search data..."
                value={searchTerm}
                onChangeText={setSearchTerm}
                borderWidth={1}
                borderColor={theme ? '#ddd' : '#444'}
                backgroundColor={theme ? '#fff' : '#333'}
                color={theme ? '#333' : '#fff'}
                placeholderTextColor={theme ? '#999' : '#666'}
                flex={1}
                minWidth={150}
              />
            </XStack>

            {/* Controls */}
            <XStack gap={8} alignItems="center">
              {enableFiltering && (
                <Button
                  size="$3"
                  variant="outlined"
                  onPress={() => setShowFilters(!showFilters)}
                  icon={<AntDesign name="filter" size={16} />}
                >
                  Filters
                </Button>
              )}

              <Button
                size="$3"
                variant="outlined"
                onPress={() => setShowBulkActions(!showBulkActions)}
                icon={<MaterialIcons name="view-column" size={16} />}
              >
                Columns
              </Button>

              {enableExport && (
                <Button
                  size="$3"
                  variant="outlined"
                  onPress={handleExport}
                  icon={<AntDesign name="download" size={16} />}
                >
                  Export
                </Button>
              )}
            </XStack>
          </XStack>

          {/* Filter row */}
          {showFilters && enableFiltering && (
            <View style={{ marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme ? '#e0e0e0' : '#333' }}>
              <XStack gap={8} flexWrap="wrap">
                {visibleColumns.filter(col => col.filterable !== false).map(col => (
                  <View key={col.key} style={{ minWidth: 150 }}>
                    <Input
                      placeholder={`Filter ${col.title}...`}
                      value={filters[col.key] || ''}
                      onChangeText={(value) => handleFilterChange(col.key, value)}
                      borderWidth={1}
                      borderColor={theme ? '#ddd' : '#444'}
                      backgroundColor={theme ? '#fff' : '#333'}
                      color={theme ? '#333' : '#fff'}
                      placeholderTextColor={theme ? '#999' : '#666'}
                      size="$3"
                    />
                  </View>
                ))}
              </XStack>
            </View>
          )}

          {/* Column visibility controls */}
          {showBulkActions && (
            <View style={{ marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme ? '#e0e0e0' : '#333' }}>
              <YStack gap={8}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#333' : '#fff' }}>
                  Column Visibility
                </Text>
                <XStack gap={8} flexWrap="wrap">
                  {columns.map(col => (
                    <TouchableOpacity
                      key={col.key}
                      onPress={() => toggleColumnVisibility(col.key)}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 16,
                        backgroundColor: columnVisibility[col.key]
                          ? (theme ? '#007bff' : '#0d6efd')
                          : (theme ? '#e9ecef' : '#444'),
                        borderWidth: 1,
                        borderColor: columnVisibility[col.key]
                          ? (theme ? '#007bff' : '#0d6efd')
                          : (theme ? '#dee2e6' : '#666')
                      }}
                    >
                      <Text style={{
                        fontSize: 12,
                        color: columnVisibility[col.key]
                          ? '#fff'
                          : (theme ? '#495057' : '#ccc')
                      }}>
                        {col.title}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </XStack>
              </YStack>
            </View>
          )}
        </View>

        {/* Table */}
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1 }}
        >
          <View style={{ minWidth: '100%' }}>
            {/* Table Header */}
            <View style={{
              flexDirection: 'row',
              backgroundColor: theme ? '#f1f3f4' : '#333',
              borderBottomWidth: 2,
              borderBottomColor: theme ? '#e0e0e0' : '#444',
              position: stickyHeader ? 'sticky' : 'relative',
              top: stickyHeader ? 0 : 'auto',
              zIndex: stickyHeader ? 10 : 1
            }}>
              {showRowNumbers && (
                <View style={{
                  width: 60,
                  padding: 12,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme ? '#e0e0e0' : '#444'
                }}>
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '600',
                    color: theme ? '#666' : '#ccc'
                  }}>
                    #
                  </Text>
                </View>
              )}

              {enableSelection && (
                <View style={{
                  width: 50,
                  padding: 12,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme ? '#e0e0e0' : '#444'
                }}>
                  <TouchableOpacity
                    onPress={() => handleSelectAll(selectedRows.size !== paginatedData.length)}
                    style={{ padding: 4 }}
                  >
                    <View style={{
                      width: 16,
                      height: 16,
                      borderWidth: 2,
                      borderColor: theme ? '#666' : '#ccc',
                      backgroundColor: selectedRows.size === paginatedData.length ? (theme ? '#007bff' : '#0d6efd') : 'transparent',
                      borderRadius: 2
                    }} />
                  </TouchableOpacity>
                </View>
              )}

              {visibleColumns.map(col => (
                <TouchableOpacity
                  key={col.key}
                  style={{
                    width: col.width || 150,
                    maxWidth: col.width || 150,
                    minWidth: col.width || 150,
                    padding: 12,
                    justifyContent: 'center',
                    alignItems: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                    borderRightWidth: 1,
                    borderRightColor: theme ? '#e0e0e0' : '#444',
                    overflow: 'hidden'
                  }}
                  onPress={() => handleSort(col.key)}
                  disabled={!enableSorting || !col.sortable}
                >
                  <XStack alignItems="center" gap={4} style={{ width: '100%', overflow: 'hidden' }}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? '#333' : '#fff',
                        textAlign: col.align || 'left',
                        flex: 1
                      }}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {col.title}
                    </Text>
                    {enableSorting && col.sortable !== false && (
                      <View>
                        {sortConfig?.key === col.key ? (
                          sortConfig.direction === 'asc' ?
                            <AntDesign name="up" size={14} color={theme ? '#007bff' : '#0d6efd'} /> :
                            <AntDesign name="down" size={14} color={theme ? '#007bff' : '#0d6efd'} />
                        ) : (
                          <View style={{ opacity: 0.3 }}>
                            <AntDesign name="up" size={14} color={theme ? '#666' : '#ccc'} />
                          </View>
                        )}
                      </View>
                    )}
                  </XStack>
                </TouchableOpacity>
              ))}

              {enableInlineActions && (
                <View style={{
                  width: 120,
                  padding: 12,
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRightWidth: 1,
                  borderRightColor: theme ? '#e0e0e0' : '#444'
                }}>
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: theme ? '#333' : '#fff'
                  }}>
                    Actions
                  </Text>
                </View>
              )}
            </View>

            {/* Table Body */}
            {paginatedData.length === 0 ? (
              <View style={{
                padding: 40,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme ? '#fff' : '#1a1a1a'
              }}>
                <Text style={{
                  fontSize: 16,
                  color: theme ? '#666' : '#999',
                  textAlign: 'center'
                }}>
                  {searchTerm || Object.values(filters).some(f => f)
                    ? 'No data matches your filters'
                    : emptyStateMessage
                  }
                </Text>
              </View>
            ) : (
              paginatedData.map((row, index) => (
                <TouchableOpacity
                  key={row._id || row.id || index}
                  style={{
                    flexDirection: 'row',
                    backgroundColor: index % 2 === 0
                      ? (theme ? '#fff' : '#1a1a1a')
                      : (theme ? '#f8f9fa' : '#222'),
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? '#e0e0e0' : '#333'
                  }}
                  onPress={() => handleRowClick(row)}
                >
                  {showRowNumbers && (
                    <View style={{
                      width: 60,
                      padding: 12,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRightWidth: 1,
                      borderRightColor: theme ? '#e0e0e0' : '#444'
                    }}>
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#666' : '#999'
                      }}>
                        {startIndex + index + 1}
                      </Text>
                    </View>
                  )}

                  {enableSelection && (
                    <View style={{
                      width: 50,
                      padding: 12,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRightWidth: 1,
                      borderRightColor: theme ? '#e0e0e0' : '#444'
                    }}>
                      <TouchableOpacity
                        onPress={() => handleRowSelect(row._id || row.id, !selectedRows.has(row._id || row.id))}
                        style={{ padding: 4 }}
                      >
                        <View style={{
                          width: 16,
                          height: 16,
                          borderWidth: 2,
                          borderColor: theme ? '#666' : '#ccc',
                          backgroundColor: selectedRows.has(row._id || row.id)
                            ? (theme ? '#007bff' : '#0d6efd')
                            : 'transparent',
                          borderRadius: 2
                        }} />
                      </TouchableOpacity>
                    </View>
                  )}

                  {visibleColumns.map(col => (
                    <View
                      key={col.key}
                      style={{
                        width: col.width || 150,
                        maxWidth: col.width || 150,
                        minWidth: col.width || 150,
                        padding: 12,
                        justifyContent: 'center',
                        alignItems: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                        borderRightWidth: 1,
                        borderRightColor: theme ? '#e0e0e0' : '#444',
                        overflow: 'hidden'
                      }}
                    >
                      {col.render ? (
                        <View style={{ width: '100%', overflow: 'hidden' }}>
                          {col.render(row[col.key], row)}
                        </View>
                      ) : (
                        <Text
                          style={{
                            fontSize: 14,
                            color: theme ? '#333' : '#fff',
                            textAlign: col.align || 'left',
                            width: '100%'
                          }}
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {row[col.key] || '-'}
                        </Text>
                      )}
                    </View>
                  ))}

                  {enableInlineActions && (
                    <View style={{
                      width: 120,
                      padding: 12,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRightWidth: 1,
                      borderRightColor: theme ? '#e0e0e0' : '#444'
                    }}>
                      <XStack gap={8}>
                        {onRowClick && (
                          <TouchableOpacity
                            onPress={() => handleRowClick(row)}
                            style={{ padding: 4 }}
                          >
                            <AntDesign name="eye" size={16} color={theme ? '#007bff' : '#0d6efd'} />
                          </TouchableOpacity>
                        )}
                        {onRowEdit && (
                          <TouchableOpacity
                            onPress={() => onRowEdit(row)}
                            style={{ padding: 4 }}
                          >
                            <AntDesign name="edit" size={16} color={theme ? '#28a745' : '#20c997'} />
                          </TouchableOpacity>
                        )}
                        {onRowDelete && (
                          <TouchableOpacity
                            onPress={() => onRowDelete(row)}
                            style={{ padding: 4 }}
                          >
                            <AntDesign name="delete" size={16} color={theme ? '#dc3545' : '#f56565'} />
                          </TouchableOpacity>
                        )}
                      </XStack>
                    </View>
                  )}
                </TouchableOpacity>
              ))
            )}
          </View>
        </ScrollView>

        {/* Pagination */}
        {totalPages > 1 && (
          <View style={{
            padding: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e0e0e0' : '#333',
            backgroundColor: theme ? '#f8f9fa' : '#2a2a2a'
          }}>
            <XStack justifyContent="space-between" alignItems="center">
              <Text style={{
                fontSize: 14,
                color: theme ? '#666' : '#999'
              }}>
                Showing {startIndex + 1}-{Math.min(endIndex, processedData.length)} of {processedData.length} results
              </Text>

              <XStack gap={8} alignItems="center">
                <Button
                  size="$3"
                  variant="outlined"
                  onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <Text style={{
                  fontSize: 14,
                  color: theme ? '#333' : '#fff',
                  minWidth: 60,
                  textAlign: 'center'
                }}>
                  {currentPage} of {totalPages}
                </Text>

                <Button
                  size="$3"
                  variant="outlined"
                  onPress={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </XStack>
            </XStack>
          </View>
        )}
      </View>
    </ErrorBoundary>
  );
};

export default ProductionDataTable;
